<template>
    <div class="lc">
        <!-- ============ EN-TÊTE COLLANT ============ -->
        <header class="lc-header">
            <div class="lc-header-top">
                <div class="lc-brand">
                    <img v-if="!logoErreur" src="/assets/logopaie.png" alt="" class="lc-logo"
                         @error="logoErreur = true">
                    <i v-else class="ri-restaurant-2-line lc-logo-fallback"></i>
                    <h1 class="lc-brand-title">{{ restaurantNom || 'Notre carte' }}</h1>
                </div>
                <div v-if="tableLabel" class="lc-table">{{ tableLabel }}</div>
            </div>

            <!-- Onglets - masqués s'il n'y a aucun plat du jour aujourd'hui -->
            <div v-if="hasDayMenu" class="lc-tabs">
                <button class="lc-tab" :class="{ active: tab === 'day' }" @click="$emit('update:tab', 'day')">
                    Menu du jour
                </button>
                <button class="lc-tab" :class="{ active: tab === 'full' }" @click="$emit('update:tab', 'full')">
                    Carte complète
                </button>
            </div>
            <p v-if="hasDayMenu && tab === 'day'" class="lc-hint">
                Sélection du chef pour aujourd'hui, {{ dateDuJour }}
            </p>

            <!-- Recherche -->
            <div v-if="items.length" class="lc-search">
                <i class="ri-search-line"></i>
                <input :value="search" type="search" placeholder="Rechercher un plat…"
                       @input="$emit('update:search', $event.target.value)">
                <button v-if="search" class="lc-search-clear" aria-label="Effacer"
                        @click="$emit('update:search', '')">
                    <i class="ri-close-line"></i>
                </button>
            </div>

            <!-- Catégories (carte complète, hors recherche) -->
            <div v-if="tab === 'full' && !search && categories.length" class="lc-cats">
                <button v-for="cat in categories" :key="cat.nom"
                        class="lc-cat" :class="{ active: activeCat === cat.nom }"
                        @click="$emit('aller-categorie', cat.nom)">
                    {{ cat.nom }}
                </button>
            </div>
        </header>

        <!-- ============ CONTENU ============ -->
        <main class="lc-main">
            <div v-if="loading" class="lc-state">
                <i class="ri-loader-4-line lc-spin"></i>
                <span>On dresse la carte…</span>
            </div>
            <div v-else-if="erreur" class="lc-state lc-state-err">
                <i class="ri-wifi-off-line"></i>
                <span>{{ erreur }}</span>
                <button class="lc-btn-ghost" @click="$emit('recharger')">Réessayer</button>
            </div>
            <div v-else-if="!items.length" class="lc-state">
                <i class="ri-inbox-line"></i>
                <span>Aucun plat disponible pour le moment.</span>
            </div>
            <div v-else-if="!sections.length" class="lc-state">
                <i class="ri-search-eye-line"></i>
                <span v-if="search">Rien ne correspond à « {{ search }} ».</span>
                <span v-else>Pas de menu du jour aujourd'hui.</span>
                <button v-if="search" class="lc-btn-ghost" @click="$emit('update:search', '')">
                    Effacer la recherche
                </button>
            </div>

            <template v-else>
                <!-- Une seule carte de plat sert aux deux onglets : le prix montré
                     est celui qui partira au panier, jamais un prix décoratif. -->
                <section v-for="sec in sections" :key="sec.nom" class="lc-sec" :id="'lc-cat-' + slug(sec.nom)">
                    <h2 class="lc-sec-title">
                        {{ sec.nom }}<span class="lc-sec-count">{{ sec.items.length }}</span>
                    </h2>

                    <div class="lc-list">
                        <article v-for="item in sec.items" :key="item.pid"
                                 class="lc-item" :class="{ 'is-out': !disponible(item) }"
                                 @click="disponible(item) && $emit('ouvrir-plat', item)">
                            <div class="lc-thumb">
                                <img v-if="item.image" :src="item.image" :alt="item.libelle" loading="lazy">
                                <div v-else class="lc-thumb-vide"><i class="ri-restaurant-line"></i></div>

                                <span v-if="platDuJour(item) && disponible(item)" class="lc-tag lc-tag-jour">
                                    Du jour
                                </span>
                                <span v-if="meilleureRemise(item) && disponible(item)" class="lc-tag lc-tag-promo">
                                    -{{ meilleureRemise(item) }}%
                                </span>
                                <div v-if="!disponible(item)" class="lc-epuise">
                                    <span>Épuisé</span>
                                </div>
                            </div>

                            <div class="lc-body">
                                <div>
                                    <h3 class="lc-name">{{ item.libelle }}</h3>
                                    <p class="lc-desc">{{ apercu(item) }}</p>
                                </div>

                                <div class="lc-bottom">
                                    <span class="lc-price">
                                        <span v-if="plusieursTarifs(item)" class="lc-price-from">dès</span>
                                        {{ fmt(prixMini(item)) }}
                                    </span>

                                    <span v-if="!disponible(item)" class="lc-add is-off">
                                        <i class="ri-close-line"></i>
                                    </span>
                                    <span v-else-if="qtyMap[item.pid]" class="lc-qty">
                                        <i class="ri-shopping-basket-2-fill"></i>{{ qtyMap[item.pid] }}
                                    </span>
                                    <span v-else class="lc-add">
                                        <i class="ri-add-line"></i>
                                    </span>
                                </div>
                            </div>
                        </article>
                    </div>
                </section>
            </template>
        </main>

        <!-- ============ BARRE PANIER FLOTTANTE ============ -->
        <transition name="lc-bar">
            <div v-if="cartCount" class="lc-cartbar" @click="$emit('voir-commande')">
                <div class="lc-cartbar-txt">
                    <span class="lc-cartbar-count">{{ cartCount }} article{{ cartCount > 1 ? 's' : '' }}</span>
                    <span class="lc-cartbar-total">{{ fmt(cartTotal) }}</span>
                </div>
                <button class="lc-cartbar-btn">
                    Voir ma commande <i class="ri-shopping-basket-2-line"></i>
                </button>
            </div>
        </transition>
    </div>
</template>

<script>
    /* eslint-disable */
    import {
        fmt, slug, disponible, platDuJour, prixMini,
        plusieursTarifs, meilleureRemise,
    } from "./format";

    export default {
        name: "lacarte",
        props: {
            restaurantNom: { type: String, default: "" },
            tableLabel: { type: String, default: "" },
            loading: { type: Boolean, default: false },
            erreur: { type: String, default: "" },
            items: { type: Array, default: () => [] },
            sections: { type: Array, default: () => [] },
            categories: { type: Array, default: () => [] },
            activeCat: { type: String, default: "" },
            tab: { type: String, default: "full" },
            hasDayMenu: { type: Boolean, default: false },
            dateDuJour: { type: String, default: "" },
            search: { type: String, default: "" },
            qtyMap: { type: Object, default: () => ({}) },
            cartCount: { type: Number, default: 0 },
            cartTotal: { type: Number, default: 0 },
        },
        emits: [
            "update:tab", "update:search", "aller-categorie",
            "ouvrir-plat", "voir-commande", "recharger",
        ],
        data: () => ({ logoErreur: false }),
        methods: {
            fmt, slug, disponible, platDuJour, prixMini, plusieursTarifs, meilleureRemise,
            // Ligne d'aperçu : la description si elle existe, sinon les accompagnements.
            apercu(item) {
                if (item.description) return item.description;
                const a = item.accompagnement || [];
                if (!a.length) return "";
                return "Au choix : " + a.slice(0, 3).map(x => x.name).join(", ") + (a.length > 3 ? "…" : "");
            },
        },
    };
</script>

<style scoped>
    /* eslint-disable */
    .lc { display: flex; flex-direction: column; min-height: 100vh; }

    /* ---------- En-tête ---------- */
    .lc-header {
        position: sticky; top: 0; z-index: 40;
        background: var(--c-surface); /* repli si color-mix n'est pas supporté */
        background: color-mix(in srgb, var(--c-surface) 92%, transparent);
        backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px);
        padding-bottom: var(--sp-sm);
    }
    .lc-header-top {
        display: flex; align-items: center; justify-content: space-between; gap: var(--sp-xs);
        padding: var(--sp-md) var(--sp-pad);
    }
    .lc-brand { display: flex; align-items: center; gap: var(--sp-xs); min-width: 0; }
    .lc-logo { height: 36px; width: auto; max-width: 104px; object-fit: contain; flex-shrink: 0; }
    .lc-logo-fallback { font-size: 1.7rem; color: var(--c-primary); }
    .lc-brand-title {
        font-size: 20px; line-height: 26px; font-weight: 800; letter-spacing: -0.01em;
        color: var(--c-primary); margin: 0;
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .lc-table {
        flex-shrink: 0; background: var(--c-primary); color: #fff;
        font-size: 13px; font-weight: 700; letter-spacing: 0.01em;
        padding: 6px 14px; border-radius: 999px; white-space: nowrap;
        box-shadow: 0 2px 6px rgba(255, 48, 8, 0.28);
    }

    /* ---------- Onglets ---------- */
    .lc-tabs {
        display: flex; gap: 4px; margin: 0 var(--sp-pad) var(--sp-sm);
        background: var(--c-surface-low); border-radius: 999px; padding: 4px;
    }
    .lc-tab {
        flex: 1; min-height: 44px; border: none; background: transparent;
        color: var(--c-on-surface-variant); font-family: inherit;
        font-size: 13px; font-weight: 700; border-radius: 999px; cursor: pointer;
        transition: background 0.18s, color 0.18s, transform 0.1s;
    }
    .lc-tab:active { transform: scale(0.96); }
    .lc-tab.active {
        background: var(--c-primary); color: #fff;
        box-shadow: 0 2px 8px rgba(255, 48, 8, 0.25);
    }
    .lc-hint {
        margin: 0 var(--sp-pad) var(--sp-sm); padding: 0 4px;
        font-size: 13px; line-height: 18px; color: var(--c-on-surface-variant);
    }

    /* ---------- Recherche ---------- */
    .lc-search { position: relative; display: flex; align-items: center; margin: 0 var(--sp-pad); }
    .lc-search > i {
        position: absolute; left: 16px; color: var(--c-on-surface-variant);
        font-size: 1.15rem; pointer-events: none;
    }
    .lc-search input {
        width: 100%; height: 52px; border: none; background: var(--c-surface);
        border-radius: 999px; padding: 0 44px 0 46px;
        font-family: inherit; font-size: 16px; color: var(--c-on-surface);
        box-shadow: 0 1px 3px rgba(42, 23, 19, 0.08);
    }
    .lc-search input::placeholder { color: var(--c-outline); }
    .lc-search input:focus { outline: 3px solid rgba(255, 48, 8, 0.18); }
    .lc-search-clear {
        position: absolute; right: 8px; width: 36px; height: 36px; border: none;
        background: transparent; color: var(--c-on-surface-variant);
        font-size: 1.15rem; cursor: pointer; display: flex;
        align-items: center; justify-content: center;
    }

    /* ---------- Catégories ---------- */
    .lc-cats {
        display: flex; gap: var(--sp-sm); overflow-x: auto;
        padding: var(--sp-sm) var(--sp-pad) 4px;
        scrollbar-width: none;
    }
    .lc-cats::-webkit-scrollbar { display: none; }
    .lc-cat {
        flex-shrink: 0; white-space: nowrap; cursor: pointer; font-family: inherit;
        padding: 9px 20px; border-radius: 999px;
        border: 2px solid var(--c-outline-variant); background: transparent;
        color: var(--c-on-surface-variant); font-size: 13px; font-weight: 700;
        transition: all 0.15s;
    }
    .lc-cat.active { background: var(--c-primary); border-color: var(--c-primary); color: #fff; }

    /* ---------- Contenu ---------- */
    .lc-main { flex: 1; padding: var(--sp-md) 0 var(--sp-xs); }

    .lc-state {
        display: flex; flex-direction: column; align-items: center; gap: var(--sp-sm);
        padding: 72px var(--sp-xl); text-align: center;
        color: var(--c-on-surface-variant); font-size: 16px;
    }
    .lc-state > i { font-size: 2.4rem; color: var(--c-outline-variant); }
    .lc-state-err > i { color: var(--c-error); }
    .lc-spin { animation: lc-rot 0.9s linear infinite; display: inline-block; }
    @keyframes lc-rot { to { transform: rotate(360deg); } }
    .lc-btn-ghost {
        margin-top: 4px; border: none; background: none; cursor: pointer; font-family: inherit;
        color: var(--c-primary); font-size: 16px; font-weight: 700;
    }

    /* ---------- Section ---------- */
    /* scroll-margin = hauteur de l'en-tête collant (253px onglet « Carte complète ») + air */
    .lc-sec { margin-bottom: var(--sp-xl); scroll-margin-top: 266px; }
    .lc-sec-title {
        display: flex; align-items: center; gap: var(--sp-xs);
        margin: 0 var(--sp-pad) var(--sp-md);
        font-size: 24px; line-height: 30px; font-weight: 800; letter-spacing: -0.01em;
        color: var(--c-on-surface);
    }
    .lc-sec-count {
        font-size: 13px; font-weight: 700; color: var(--c-primary);
        background: var(--c-surface-container); border-radius: 999px; padding: 3px 10px;
    }

    /* ---------- Carte plat ---------- */
    .lc-list { display: flex; flex-direction: column; gap: var(--sp-md); padding: 0 var(--sp-pad); }
    .lc-item {
        display: flex; gap: var(--sp-md); padding: var(--sp-sm);
        background: var(--c-surface); border: 1px solid var(--c-outline-variant);
        border-radius: 16px; cursor: pointer;
        transition: transform 0.12s, box-shadow 0.18s;
    }
    .lc-item:active { transform: scale(0.985); }
    .lc-item.is-out { opacity: 0.6; cursor: default; }

    .lc-thumb {
        position: relative; width: 112px; height: 112px; flex-shrink: 0;
        border-radius: 14px; overflow: hidden; background: var(--c-surface-container);
    }
    .lc-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .lc-thumb-vide {
        width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;
        color: var(--c-outline); font-size: 2rem;
        background: linear-gradient(135deg, var(--c-surface-container), var(--c-surface-high));
    }
    .lc-tag {
        position: absolute; left: 8px;
        font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.06em;
        padding: 3px 8px; border-radius: 999px;
    }
    .lc-tag-jour { top: 8px; background: var(--c-primary); color: #fff; }
    .lc-tag-promo { bottom: 8px; background: var(--c-accent-warm); color: var(--c-on-surface); }
    .lc-epuise {
        position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
        background: rgba(42, 23, 19, 0.45);
    }
    .lc-epuise span {
        background: var(--c-surface); color: var(--c-on-surface);
        font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.04em;
        padding: 4px 12px; border-radius: 999px;
    }

    .lc-body {
        flex: 1; min-width: 0; display: flex; flex-direction: column;
        justify-content: space-between; padding: 4px 0;
    }
    .lc-name {
        margin: 0; font-size: 18px; line-height: 24px; font-weight: 700;
        color: var(--c-on-surface);
        display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
    }
    .lc-desc {
        margin: 4px 0 0; font-size: 13px; line-height: 18px; color: var(--c-on-surface-variant);
        display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
    }
    .lc-bottom { display: flex; align-items: flex-end; justify-content: space-between; gap: var(--sp-xs); margin-top: var(--sp-xs); }
    .lc-price {
        font-size: 18px; line-height: 24px; font-weight: 700; color: var(--c-primary);
        font-variant-numeric: tabular-nums; white-space: nowrap;
    }
    .lc-price-from {
        font-size: 12px; font-weight: 600; color: var(--c-on-surface-variant);
        margin-right: 2px;
    }
    .lc-add, .lc-qty {
        display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        height: 40px; min-width: 40px; border-radius: 999px;
        background: var(--c-primary); color: #fff; font-size: 1.25rem;
        box-shadow: 0 2px 8px rgba(255, 48, 8, 0.3);
    }
    .lc-qty { gap: 5px; padding: 0 14px; font-size: 15px; font-weight: 800; font-variant-numeric: tabular-nums; }
    .lc-qty i { font-size: 1rem; }
    .lc-add.is-off { background: var(--c-surface-high); color: var(--c-outline); box-shadow: none; }

    /* ---------- Barre panier ---------- */
    .lc-cartbar {
        position: fixed; left: 0; right: 0; bottom: 0; z-index: 60;
        margin: 0 var(--sp-md) calc(var(--sp-lg) + env(safe-area-inset-bottom, 0px));
        display: flex; align-items: center; justify-content: space-between; gap: var(--sp-xs);
        padding: 8px 8px 8px 22px; cursor: pointer;
        background: var(--c-inverse-surface);
        background: color-mix(in srgb, var(--c-inverse-surface) 94%, transparent);
        backdrop-filter: blur(18px); -webkit-backdrop-filter: blur(18px);
        border-radius: 999px; box-shadow: 0 10px 26px rgba(42, 23, 19, 0.35);
    }
    .lc-cartbar-txt { display: flex; flex-direction: column; min-width: 0; }
    .lc-cartbar-count {
        font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
        color: rgba(255, 255, 255, 0.7);
    }
    .lc-cartbar-total {
        font-size: 18px; line-height: 24px; font-weight: 700; color: #fff;
        font-variant-numeric: tabular-nums;
    }
    .lc-cartbar-btn {
        flex-shrink: 0; border: none; cursor: pointer; font-family: inherit;
        background: var(--c-primary); color: #fff; font-size: 13px; font-weight: 700;
        padding: 0 22px; height: 48px; border-radius: 999px;
        display: flex; align-items: center; gap: 8px;
    }
    .lc-cartbar-btn i { font-size: 1.1rem; }

    .lc-bar-enter-active, .lc-bar-leave-active { transition: transform 0.28s cubic-bezier(0.2, 0.9, 0.3, 1), opacity 0.2s; }
    .lc-bar-enter-from, .lc-bar-leave-to { transform: translateY(120%); opacity: 0; }
</style>
